/*
 * DebugInfoCollector owns statCollector sections for the 3D viewer debug panel.
 *
 * It provides a small registry of named stat collectors that rendering and
 * loading components can update directly without routing debug data through
 * the dialog component.
 */
import {statCollector} from "@/engine/helpers/statCollector.js";

const SECTION_DEFINITIONS = [
    {id: "summary", title: "Summary"},
    {id: "camera", title: "Camera"},
    {id: "renderer", title: "Renderer"},
    {id: "tileset", title: "Tileset"},
    {id: "grid", title: "Grid", formatters: {referenceGrid: "json", boundsBox: "json"}},
];

export class DebugInfoCollector {
    /**
     * Creates a structured collector for 3D viewer debug information.
     *
     * @returns {DebugInfoCollector} Initialized collector with empty stat sections.
     */
    constructor() {
        this.sections = SECTION_DEFINITIONS.map((section) => ({
            ...section,
            stat: new statCollector(section.id, {}, null, section.formatters),
        }));
        this.cameraAdapter = null;
        this.replaceSection("summary", {state: "No model loaded"});
    }

    /**
     * Returns all configured debug sections with their stat collectors.
     *
     * @returns {Array<{id: string, title: string, stat: statCollector}>} Ordered debug sections.
     */
    getSections() {
        return this.sections;
    }

    /**
     * Returns a stat collector for one debug section.
     *
     * @param {string} sectionId Section identifier from SECTION_DEFINITIONS.
     * @returns {statCollector|null} Section stat collector, or null when the section is unknown.
     */
    getStat(sectionId) {
        return this._section(sectionId)?.stat || null;
    }

    /**
     * Returns a keyed object of stat collectors for component injection.
     *
     * @param {string[]} [sectionIds] Optional section ids to include; all sections are returned when omitted.
     * @returns {Object<string, statCollector>} Object keyed by section id.
     */
    getStats(sectionIds = null) {
        const ids = Array.isArray(sectionIds) ? new Set(sectionIds) : null;

        return Object.fromEntries(
            this.sections
                .filter((section) => !ids || ids.has(section.id))
                .map((section) => [section.id, section.stat])
        );
    }

    /**
     * Binds a display container to one debug section.
     *
     * @param {string} sectionId Section identifier from SECTION_DEFINITIONS.
     * @param {HTMLElement} container DOM node where statCollector should render.
     * @returns {void}
     */
    setDisplayContainer(sectionId, container) {
        const section = this._section(sectionId);

        if (section) {
            section.stat.setDisplayContainer(container);
        }
    }

    /**
     * Replaces values in one debug section.
     *
     * @param {string} sectionId Section identifier from SECTION_DEFINITIONS.
     * @param {object|null|undefined} data New key-value data for the section.
     * @returns {void}
     */
    replaceSection(sectionId, data) {
        const section = this._section(sectionId);

        if (section) {
            section.stat.replace(compactObject(data));
        }
    }

    /**
     * Stores the active viewer adapter used by camera debug controls.
     *
     * @param {object|null} adapter Viewer exposing camera debug and restore methods.
     * @returns {void}
     */
    setCameraAdapter(adapter) {
        this.cameraAdapter = adapter || null;
    }

    /**
     * Returns the active viewer adapter used by camera debug controls.
     *
     * @returns {object|null} Current camera adapter, or null before a viewer is ready.
     */
    getCameraAdapter() {
        return this.cameraAdapter;
    }

    /**
     * Re-renders all visible collector sections.
     *
     * @returns {void}
     */
    show() {
        this.sections.forEach((section) => section.stat.show());
    }

    /**
     * Finds a configured section by id.
     *
     * @param {string} sectionId Section identifier.
     * @returns {{id: string, title: string, stat: statCollector}|null} Section descriptor, or null when not found.
     */
    _section(sectionId) {
        return this.sections.find((section) => section.id === sectionId) || null;
    }
}

/**
 * Removes null and undefined values while preserving nested objects for display.
 *
 * @param {object|null|undefined} data Source section data.
 * @returns {object} Compact object safe to pass into statCollector.replace().
 */
function compactObject(data) {
    if (!data || typeof data !== "object") {
        return {};
    }

    return Object.fromEntries(
        Object.entries(data).filter(([, value]) => value !== null && value !== undefined)
    );
}
